import { Minus, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateCart } from '~/api';
import type { Location, Maybe, OrderItem } from '~/graphql/graphql';
import { useDebounce } from '~/hooks/useDebounce';
import { setCarts } from '~/redux/reducers/cart';
import type { RootState } from '~/redux/store';
import { formatCurrency } from '~/utils';
import { getGradeInformation } from '~/utils/order';

interface CartItemProps {
  item: OrderItem;
  cartId: string;
  sellingOrganizationId: string;
  shippingLocation?: Maybe<Location>;
}

export function CartListItem({
  item,
  cartId,
  sellingOrganizationId,
  shippingLocation,
}: Readonly<CartItemProps>) {
  const [deletingCart, setDeletingCart] = useState(false);

  const dispatch = useDispatch();

  const { t } = useTranslation('cart');

  const carts = useSelector((state: RootState) => state.cart.carts);

  const { mutate: updateCart } = useUpdateCart();

  const handleAddToCart = (productId: string, quantity: string) => {
    updateCart({
      sellingOrganizationId: sellingOrganizationId,
      productId: productId,
      quantity: { value: Number(quantity), unit: 'MBF' },
      locationId: shippingLocation?.id,
    });
  };

  const debouncedAddToCart = useDebounce(handleAddToCart, 1000);

  const onUpdateQuantity = (productId: string, newQuantity: number) => {
    const cartIndex = carts.findIndex((cart) => cart.id === cartId);
    if (cartIndex === -1) return;
    const updatedCarts = carts.map((cart, index) => {
      if (index !== cartIndex) return cart;
      const updatedCart = {
        ...cart,
        items: cart.items?.map((item) =>
          item.platformProductsLengths?.id === productId
            ? { ...item, quantity: { ...item.quantity, value: newQuantity } }
            : item
        ),
      };
      return updatedCart;
    });

    dispatch(setCarts(updatedCarts));
    debouncedAddToCart(productId, newQuantity.toString());
  };
  const onRemoveItem = (productId: string) => {
    setDeletingCart(true);
    updateCart(
      {
        sellingOrganizationId: sellingOrganizationId,
        productId: productId,
        quantity: { value: 0, unit: 'MBF' },
        locationId: shippingLocation?.id,
      },
      {
        onSettled: () => setDeletingCart(false),
      }
    );
  };

  return (
    <div className="p-4 sm:p-6 border-b border-neut-200 last:border-b-0">
      <div className="flex gap-4">
        <div className="w-16 h-16 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <div className="text-2xl">🌲</div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-neut-prim font-semibold mb-1 truncate">
                {item.platformProductsLengths?.platformProduct?.name}
              </h4>
              <p className="text-sm text-neut-sec mb-1 ">
                {t('cart.cart_grade')}:{' '}
                {getGradeInformation(item.platformProductsLengths)}
                <span className="ml-2 px-2 py-1 bg-success-100 text-success-600 rounded-md">
                  {item.platformProductsLengths.length.displayValue}
                </span>
              </p>
            </div>

            <button
              aria-label="Remove"
              onClick={() =>
                onRemoveItem(item.platformProductsLengths?.id ?? '')
              }
              className="p-2 hover:bg-neut-hover rounded-lg transition-colors flex-shrink-0 ml-2 flex items-center justify-center cursor-pointer"
            >
              <Trash2
                className="w-4 h-4 text-neut-400 hover:text-error-500"
                aria-hidden="true"
              />{' '}
              {deletingCart ? (
                <span className="text-xs text-error-500 ml-1">
                  {t('cart.deleting')}
                </span>
              ) : null}
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  onUpdateQuantity(
                    item.platformProductsLengths?.id ?? '',
                    Math.max(0, item.quantity.value - 1)
                  )
                }
                className="w-8 h-8 rounded-lg border border-neut-300 flex items-center justify-center hover:bg-neut-hover transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-neut-600"
                disabled={item.quantity.value <= 1}
              >
                <Minus className="w-3.5 h-3.5" />
              </button>

              <div className="text-center min-w-[60px]">
                <p className="text-sm text-neut-prim font-medium">
                  {item.quantity.value} {item?.quantity.unit}
                </p>
              </div>

              <button
                onClick={() =>
                  onUpdateQuantity(
                    item?.platformProductsLengths?.id ?? '',
                    item.quantity.value + 1
                  )
                }
                className="w-8 h-8 rounded-lg border border-neut-300 flex items-center justify-center hover:bg-neut-hover transition-colors text-neut-600"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="text-right">
              <p className="text-xs text-neut-sec mb-0.5">
                ${item.price}/{item?.quantity.unit}
              </p>
              <p className="text-lg text-neut-prim font-semibold">
                {formatCurrency(item.quantity.value * item.price)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
